/**
 * Hero „Galaxie“: lädt die WebGL-Milchstraße (galaxy-gl.js, eigener Chunk) erst nach dem Seitenladen im Leerlauf,
 * damit der Slogan (LCP) nicht wartet. Bis dahin – und ohne WebGL oder bei „Daten sparen“ – trägt der CSS-Himmel.
 *
 * Steuerung:
 *  - läuft nur, solange der Hero sichtbar ist und der Tab im Vordergrund liegt (der Hero ist sticky: nach dem ersten
 *    Bildschirm liegt er verdeckt unter den Abschnitten → anhalten, sobald er ganz überdeckt ist)
 *  - Maus/Finger bewegen die Galaxie (Neigung) und verwirbeln die Sterne unter dem Zeiger
 *  - Klick/Tipp in den Himmel sendet eine Druckwelle
 *  - Scrollen fährt die Kamera hinein und blendet ab
 *  - „Weniger Bewegung“: ein ruhendes Bild, keine Animation, keine Zeigereffekte
 */
import { qs } from '../utils/dom.js';

/**
 * WebGL mit echter Grafikkarte? Software-Renderer (SwiftShader, llvmpipe, Microsoft Basic Render) rechnen die Sterne
 * auf dem Prozessor – das ruckelt und blockiert die Seite. Dort bleibt der CSS-Himmel. Trifft auch Test-Tools wie
 * Lighthouse/PageSpeed, die ohne Grafikkarte laufen.
 */
function webglUsable() {
	try {
		const c = document.createElement('canvas');
		const gl = c.getContext('webgl') || c.getContext('experimental-webgl');
		if (!gl) return false;
		const info = gl.getExtension('WEBGL_debug_renderer_info');
		const renderer = String(info ? gl.getParameter(info.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER));
		gl.getExtension('WEBGL_lose_context')?.loseContext();
		return !/swiftshader|llvmpipe|softpipe|software|basic render/i.test(renderer);
	} catch {
		return false;
	}
}

export function init() {
	const hero = qs('[data-pa-hero]');
	const canvas = hero && qs('[data-pa-galaxy]', hero);
	if (!canvas) return;
	if (navigator.connection?.saveData || !webglUsable()) return;

	const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

	const start = async () => {
		let galaxy;
		try {
			const { createGalaxy } = await import('./galaxy-gl.js');
			galaxy = createGalaxy(canvas, {
				reducedMotion: reduced.matches,
				onReady: () => canvas.classList.add('is-ready'),
			});
		} catch (err) {
			console.warn('[pa-hero] Galaxie nicht verfügbar:', err);   // CSS-Himmel bleibt stehen
			return;
		}
		if (!galaxy) return;

		const covered = () => window.scrollY > hero.offsetHeight + 40;
		const sync = () => galaxy.setRunning(!document.hidden && !covered());

		let ticking = false;
		window.addEventListener('scroll', () => {
			if (ticking) return;
			ticking = true;
			window.requestAnimationFrame(() => {
				galaxy.setScroll(Math.min(1, window.scrollY / Math.max(1, hero.offsetHeight)));
				sync();
				ticking = false;
			});
		}, { passive: true });
		document.addEventListener('visibilitychange', sync);

		window.addEventListener('pointermove', (e) => galaxy.pointer(e.clientX, e.clientY), { passive: true });
		document.documentElement.addEventListener('pointerleave', () => galaxy.pointerLeave());
		hero.addEventListener('pointerdown', (e) => {
			if (e.button !== 0 || e.target.closest('a, button')) return;
			galaxy.pulse(e.clientX, e.clientY);
		});
		reduced.addEventListener('change', (e) => { galaxy.setReducedMotion(e.matches); sync(); });

		galaxy.setScroll(Math.min(1, window.scrollY / Math.max(1, hero.offsetHeight)));
		sync();
	};

	const kick = () => ('requestIdleCallback' in window
		? window.requestIdleCallback(start, { timeout: 1500 })
		: window.setTimeout(start, 200));

	if (document.readyState === 'complete') kick();
	else window.addEventListener('load', kick, { once: true });
}
